import { DialogContent, Table, TableBody, TableCell, TableRow } from '@mui/material';

import DialogWrapper from './Wrapper';
import { getCurrencyView } from '../../../utils/format';

export type Payout = {
  amount?: number;
  created_at?: string;
  currency?: string;
  error?: PayoutError;
  fees?: PayoutFee[];
  live: boolean;
  metadata?: Record<string, string>;
  payment_details?: PayoutPaymentDetails;
  payout_id?: string;
  recipient?: PayoutRecipient;
  recipient_account?: PayoutRecipientAccount;
  sender?: PayoutSender;
  sender_account?: PayoutSenderAccount;
  status?: string;
  scheduled_date?: string;
  transaction_date?: string;
  transaction_time?: string | null;
  type?: string;
  updated_at?: string;
};

type PayoutError = {
  details: string;
  error_code: string;
  message: string;
  type: string;
};

type PayoutFee = {
  amount: number;
  currency?: string;
  paid_by?: string;
  paid_by_account_id?: string;
};

type PayoutPaymentDetails = {
  description?: string;
  estimated_arrival_time?: string | null;
  mandate_id?: string;
  processor_details?: PayoutProcessorDetails;
  references?: PayoutReferences;
  scheduled_date?: string;
  external_transaction_reference?: string;
};

type PayoutProcessorDetails = {
  processor_id?: string;
  processor_reference?: string;
};

type PayoutReferences = {
  recipient_reference?: string;
};

type PayoutRecipient = {
  external_user_id: string;
  id?: string;
  name?: string;
  sender_account?: PaymentAccount;
  sender_details?: PayoutUserDetail[];
  sender_type?: string;
};

type PayoutUserDetail = {
  details_type?: string;
  values?: string[];
};

type PayoutRecipientAccount = {
  account_id?: string;
  account_number?: PayoutAccountNumber;
  account_number_masked?: string;
  account_type?: string;
  accountholder_name?: string;
  accountholder_name_plaintext?: string | null;
  bank_code?: string;
  institution_id?: string;
  institution_name?: string;
  metadata?: Record<string, string>;
  user_id?: string;
};

type PayoutAccountNumber = {
  number: string;
  number_plaintext?: string | null;
  type: string;
};

type PayoutSender = {
  name: string;
  recipient_account_id?: string;
};

type PayoutSenderAccount = {
  account_id?: string;
  account_type?: string;
  settlement_account_reference?: string;
};

type PaymentAccount = {
  institution_id?: string;
  institution_name?: string;
};

type ModalProps<K> = {
  open: boolean;
  handleClose: () => void;
  details?: K;
  customerAppId: string;
};

export function PayoutDetailsModal({ open, handleClose, details: payout, customerAppId }: ModalProps<Payout>) {
  if (payout === undefined) {
    return <></>;
  }

  const fields: { label: string; value: string | null | undefined }[] = [
    { label: 'Amount', value: getCurrencyView(payout.currency ?? '', payout.amount) },
    { label: 'Payout Type', value: payout.type },
    { label: 'Payment Processor', value: payout.payment_details?.processor_details?.processor_id },
    { label: 'External Transaction Reference', value: payout.payment_details?.external_transaction_reference },
    { label: 'Description', value: payout.payment_details?.description },
    { label: 'Recipient Name', value: payout.recipient?.name },
    { label: 'Recipient Bank', value: payout.recipient_account?.institution_name },
    { label: 'Recipient Account Number', value: payout.recipient_account?.account_number_masked },
    { label: 'Recipient External User ID', value: payout.recipient?.external_user_id },
    { label: 'Recipient User ID', value: payout.recipient?.id },
    { label: 'Recipient Account ID', value: payout.recipient_account?.account_id },
    { label: 'Sender Name', value: payout.sender?.name },
    { label: 'Sender Account ID', value: payout.sender_account?.account_id },
    { label: 'Settlement Account Reference', value: payout.sender_account?.settlement_account_reference },
    { label: 'Scheduled Date', value: payout.payment_details?.scheduled_date },
    { label: 'Transaction Time', value: payout?.transaction_time },
    { label: 'Customer App ID', value: customerAppId },
  ];

  return (
    <DialogWrapper id="" onClose={handleClose} title="Payout Details" open={open}>
      <DialogContent dividers>
        <Table>
          <TableBody>
            {fields
              .filter((field) => field.value)
              .map((field) => (
                <TableRow key={field.label}>
                  <TableCell>{field.label}</TableCell>
                  <TableCell align="right">{field.value}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </DialogContent>
    </DialogWrapper>
  );
}
